import { createContext, useContext, type ComponentType, type ReactNode } from 'react';
import type { Editor, JSONContent } from '@tiptap/core';

export interface PageRef {
  id: string;
  title: string;
  icon: string | null;
}

/**
 * Everything the editor needs from the app, so the editor package stays free of
 * storage and routing concerns. Hooks (`use*`) may be called from node views.
 */
export interface EditorHost {
  /** Pages matching a query, best first (titles and aliases). */
  searchPages(query: string, limit?: number): PageRef[];
  /** Live page by id (re-renders on rename). */
  usePage(id: string | null): PageRef | null;
  findPageByName(name: string): PageRef | null;
  openPage(id: string, newTab?: boolean): void;
  createPage(title: string): Promise<PageRef>;
  openTag(tag: string): void;
  listTags(query: string): string[];
  /** Content of another page, for `![[embeds]]` (undefined while loading). */
  useEmbedContent(pageId: string | null): JSONContent | null | undefined;
  /** An attached file by link target: object URL + type (null = not found, undefined = loading). */
  useAttachment(
    target: string | null,
  ): { url: string; mime: string; name: string } | null | undefined;
  /** Store a pasted, dropped or picked file; returns its name for `![[name]]` (null = refused). */
  uploadFile?: (file: File) => Promise<string | null>;
  /** Open or save an attached file with the system. */
  openAttachment(target: string): void;
  t(key: string, vars?: Record<string, unknown>): string;
  /** Writing help at the selection or cursor (the /AI command); absent = no AI. */
  aiWrite?: (editor: Editor) => void;
  /** Pick a template and insert its text at the cursor (the /sjabloon command). */
  insertTemplate?: (editor: Editor) => void;
  /** Turn the checkbox task with this id (on the open page) into a task page; absent = not offered. */
  promoteTask?: (taskId: string) => void;
  /** Draws a page icon (emoji or uploaded image); absent = the icon as text. */
  PageIcon?: ComponentType<{ icon: string | null; fallback?: ReactNode }>;
  /** Live results under a ```tasks query block (Obsidian Tasks syntax); absent = code only. */
  TaskQuery?: ComponentType<{ query: string }>;
}

export const EditorHostContext = createContext<EditorHost | null>(null);

export function useHost(): EditorHost {
  const host = useContext(EditorHostContext);
  if (!host) throw new Error('EditorHostContext missing');
  return host;
}
